export interface ListeningAudioPosition {
  audioIndex: number;
  currentTime: number;
}

interface StoredListeningAudioPosition extends ListeningAudioPosition {
  source: string;
}

const storagePrefix = 'ielts:mock-listening-audio:v1:';
const initialPosition: ListeningAudioPosition = {audioIndex: 0, currentTime: 0};

function hasValidSources(sources: string[]): boolean {
  return Array.isArray(sources) && sources.every(source => typeof source === 'string');
}

function hasValidPosition(position: unknown, sources: string[]): position is ListeningAudioPosition {
  if (!position || typeof position !== 'object' || !hasValidSources(sources)) {
    return false;
  }

  const {audioIndex, currentTime} = position as ListeningAudioPosition;
  if (!Number.isInteger(audioIndex) || audioIndex < 0 || audioIndex > sources.length || !Number.isFinite(currentTime) || currentTime < 0) {
    return false;
  }

  return audioIndex !== sources.length || currentTime === 0;
}

function isValidStoredPosition(position: unknown, sources: string[]): position is StoredListeningAudioPosition {
  if (!hasValidPosition(position, sources)) {
    return false;
  }

  const {audioIndex, source} = position as StoredListeningAudioPosition;
  return typeof source === 'string' && (audioIndex === sources.length ? source === '' : source === sources[audioIndex]);
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getStoredPosition(examKey: string, sources: string[]): ListeningAudioPosition | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const value = storage.getItem(`${storagePrefix}${examKey}`);
    if (!value) {
      return null;
    }

    const position: unknown = JSON.parse(value);
    return isValidStoredPosition(position, sources)
      ? {audioIndex: position.audioIndex, currentTime: position.currentTime}
      : null;
  } catch {
    return null;
  }
}

function isNewerPosition(candidate: ListeningAudioPosition, position: ListeningAudioPosition): boolean {
  return candidate.audioIndex > position.audioIndex
    || (candidate.audioIndex === position.audioIndex && candidate.currentTime > position.currentTime);
}

export function readListeningAudioPosition(examKey: string, sources: string[], fallback: ListeningAudioPosition): ListeningAudioPosition {
  const safeFallback = hasValidPosition(fallback, sources) ? fallback : initialPosition;
  const storedPosition = getStoredPosition(examKey, sources);

  if (storedPosition && isNewerPosition(storedPosition, safeFallback)) {
    return storedPosition;
  }

  return {audioIndex: safeFallback.audioIndex, currentTime: safeFallback.currentTime};
}

export function writeListeningAudioPosition(examKey: string, sources: string[], position: ListeningAudioPosition): void {
  if (!hasValidPosition(position, sources)) {
    return;
  }

  const storage = getStorage();
  if (!storage) {
    return;
  }

  const source = position.audioIndex === sources.length ? '' : sources[position.audioIndex];
  try {
    storage.setItem(`${storagePrefix}${examKey}`, JSON.stringify({...position, source}));
  } catch {
    // Keep playback usable when storage is disabled or quota-limited.
  }
}
